import React, { useEffect } from 'react';
import {
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { VERIFY_OPTION } from 'shared/constants/ekyc';
import Button from 'shared/modules/common/components/atoms/Button';
import Icon from 'shared/modules/common/components/atoms/Icon';
import SimpleRadio from 'shared/modules/common/components/atoms/SimpleRadio';
import generateID from 'shared/modules/common/utils/generate-id';

const AskToVerifyStage = (props: {
  onNextButtonPress: () => void;
  selectedOption: number;
  onOptionSelect: (option: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}) => {
  const {
    onNextButtonPress,
    selectedOption,
    onOptionSelect,
    onFocus,
    onBlur,
  } = props;

  // Dynamic style of the option based on selection
  const getOptionViewStyle = (checked: boolean) => {
    if (checked) {
      return styles.optionViewSelected;
    } else {
      return styles.optionViewUnselected;
    }
  };

  useEffect(() => {
    onFocus?.();

    return () => onBlur?.();
  }, []);

  return (
    <View style={styles.root}>
      <ScrollView style={styles.scrollView}>
        <Text style={styles.mainTitle}>{`本人確認でもっと便利に`} </Text>

        {/* Sub Title */}
        <View style={styles.subTitleView}>
          <Icon
            name="verified"
            size="24"
            tintColor={'#2a2e32'}
            style={{ marginRight: 10 }}
          />
          <View>
            <Text
              style={styles.subTitle}
            >{`150万人以上のお客様が本人確認済み`}</Text>
          </View>
        </View>

        {/* Second Subtitle */}
        <View style={styles.subTitleView}>
          <Icon
            name="complete-easily"
            size="24"
            tintColor={'#2a2e32'}
            style={{ marginRight: 10 }}
          />
          <Text style={styles.subTitle}>{`最短5分で完了　※別途審査あり`}</Text>
        </View>

        {/* OPTION First*/}
        <TouchableOpacity
          onPress={() => {
            onOptionSelect(VERIFY_OPTION.NOW);
          }}
          {...generateID('ask-to-verify-stage-option-1')}
        >
          <View
            style={getOptionViewStyle(selectedOption === VERIFY_OPTION.NOW)}
          >
            <View style={styles.shieldView}>
              <View
                style={(styles.firstElementInRow, { flexDirection: 'row' })}
              >
                <Text
                  style={styles.optionTextBold}
                >{`今すぐ本人確認する`}</Text>
                <Icon name="shield" size="16" style={styles.shieldIcon} />
              </View>

              <View style={styles.lastElementInRow}>
                <SimpleRadio checked={selectedOption === VERIFY_OPTION.NOW} />
              </View>
            </View>

            {/* Dotted row */}
            <View style={styles.dottedStyle} />

            {/* feature row 1*/}
            <View style={styles.iconWithTextView}>
              <Icon name="mobile" size="24" style={styles.mobileIcon} />
              <Text
                style={styles.optionText}
              >{`メアドと携帯番号でお買い物`}</Text>
            </View>

            {/* feature row 3*/}
            <View style={styles.iconWithTextViewPd12}>
              <Icon name="circle-yen" size="24" style={styles.circleYenIcon} />
              <Text
                style={styles.optionText}
              >{`3・6・12回あと払いが分割手数料無料*\n（口座振替・銀行振込のみ無料）`}</Text>
            </View>
          </View>
        </TouchableOpacity>

        {/* OPTION Second*/}
        <TouchableOpacity
          onPress={() => {
            onOptionSelect(VERIFY_OPTION.LATER);
          }}
          {...generateID('ask-to-verify-stage-option-2')}
        >
          <View
            style={getOptionViewStyle(selectedOption === VERIFY_OPTION.LATER)}
          >
            <View style={styles.optionTwoView}>
              <View style={styles.firstElementInRow}>
                <Text
                  style={styles.optionTextBold}
                >{`本人確認しないで始める`}</Text>
              </View>
              <View style={styles.lastElementInRow}>
                <SimpleRadio checked={selectedOption === VERIFY_OPTION.LATER} />
              </View>
            </View>
            <View style={styles.dottedStyle} />

            <View style={styles.iconWithTextViewPd0}>
              <Icon name="mobile" size="24" style={styles.mobileIcon} />
              <Text
                style={styles.optionText}
              >{`メアドと携帯番号でお買い物`}</Text>
            </View>
          </View>
        </TouchableOpacity>

        <Text style={styles.disclaimer}>
          {`*選択できる支払い回数は加盟店により異なります。\n一部のショップでは3・6・12回あと払いはご利用いただけません。`}
        </Text>
      </ScrollView>

      <View style={styles.buttonContainer}>
        <Button
          variant="primary"
          title="次へ"
          onPress={onNextButtonPress}
          id="ekyc-onboarding-verify-stage-next-button"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  scrollView: {
    flexGrow: 1,
    marginBottom: 24,
  },
  mainTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 24,
    color: '#000',
  },
  subTitleView: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 24,
  },
  subTitle: {
    fontSize: 16,
    color: '#101825',
    fontWeight: '300',
  },
  optionViewSelected: {
    padding: 24,
    borderWidth: 1.5,
    borderColor: '#E30E80',
    borderRadius: 16,
    backgroundColor: '#fff',
    marginBottom: 24,
  },
  optionViewUnselected: {
    padding: 24,
    borderWidth: 1.5,
    borderColor: '#fff',
    borderRadius: 16,
    backgroundColor: '#fff',
    marginBottom: 24,
  },
  shieldView: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  firstElementInRow: {
    flex: 8,
    alignItems: 'flex-start',
  },
  optionTextBold: {
    fontSize: 13,
    lineHeight: 16,
    fontWeight: '600',
  },
  optionText: {
    fontSize: 13,
    lineHeight: 16,
    fontWeight: '300',
  },
  shieldIcon: {
    width: 16,
    height: 16,
    marginLeft: 8,
  },
  lastElementInRow: {
    flex: 5,
    alignItems: 'flex-end',
  },
  dottedStyle: {
    height: 1,
    backgroundColor: '#e1e1e1',
    marginVertical: 16,
  },
  iconWithTextView: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 20,
  },
  mobileIcon: {
    width: 24,
    height: 24,
    marginRight: 16,
  },
  iconWithTextViewPd12: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 12,
  },
  circleYenIcon: {
    width: 24,
    height: 24,
    marginRight: 16,
  },
  optionTwoView: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconWithTextViewPd0: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  disclaimer: {
    fontSize: 13,
    color: '#1C1C1C',
    paddingBottom: 24,
  },
  buttonContainer: {
    flex: 1,
    flexDirection: 'row',
    flexGrow: 1,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
});

export default AskToVerifyStage;
